<script setup lang="ts">
/**
 * Survey answer component for yes/no questions with associated products.
 */
import {YesNoWithProductsQuestion} from "@/core/interfaces/survey/Survey";
import SurveyAnswerButton from "@/survey/components/survey-modal/answers/SurveyAnswerButton.vue";
import {computed, ref} from "vue";
import ConsumerProductChip from "@/drug-interaction/components/header/ConsumerProductChip.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {SurveyAnswerComponentEmit, SurveyAnswerComponentProps} from "@/survey/components/survey-modal/answers/index";

const {question} = defineProps<SurveyAnswerComponentProps<YesNoWithProductsQuestion>>();
const emits = defineEmits<SurveyAnswerComponentEmit<YesNoWithProductsQuestion>>();

const localQuestion = ref(question);

/**
 * On value press, either yes or no
 * If no, emit the value directly
 * If yes, toggle the value and wait for product selection
 */
const onPress = (value: boolean): void => {
    if (!value) {
        emits('onPress', {value: {value: [false], products: null}});
    } else {
        if (localQuestion.value.user_answer?.value.value.includes(true)) {
            localQuestion.value.user_answer = null;
        } else {
            localQuestion.value.user_answer = {value: {value: [value], products: []}};
        }
    }
};

/**
 * Tell if the user selected yes
 */
const isYes = computed(() => {
    return localQuestion.value.user_answer?.value.value.includes(true);
});

/**
 * Get the products associated to the "yes" answer
 */
const products = computed(() => {
    const yesAnswer = question.answers.find(answer => answer.value);

    return yesAnswer?.products || [];
});

/**
 * Select or deselect a product
 */
const selectProduct = (product: { name: string; id: number }): void => {
    if (!localQuestion.value.user_answer) {
        localQuestion.value.user_answer = {value: {value: [true], products: [product.id]}};
    } else {
        if (isProductSelected(product)) {
            localQuestion.value.user_answer.value.products = localQuestion.value.user_answer.value.products?.filter(p => p !== product.id) ?? [];
        } else {
            if (!localQuestion.value.user_answer.value.products) {
                localQuestion.value.user_answer.value.products = [];
            }
            localQuestion.value.user_answer.value.products.push(product.id);
        }
    }
};

/**
 * Tell if a product is selected
 */
const isProductSelected = (product: { name: string; id: number }) => {
    return localQuestion.value.user_answer?.value.products?.includes(product.id) || false;
};

/**
 * Submit the answer with the selected products
 */
const submit = () => {
    if (!localQuestion.value?.user_answer?.value) {
        return;
    }

    const productsId = localQuestion.value.user_answer?.value.products ?? [];

    emits('onPress', {
        value: {
            value: localQuestion.value.user_answer.value.value,
            products: productsId
        }
    });
};
</script>

<template>
  <div>
    <div class="flex flex-wrap gap-5 justify-center">
      <survey-answer-button
        v-for="answer in question.answers"
        :key="answer.value.toString()"
        :class="[{'bg-primary': answer.value && isYes}]"
        @click="onPress(answer.value)"
      >
        <p class="font-content">
          {{ answer.label }}
        </p>
      </survey-answer-button>
    </div>
    <div>
      <div v-if="products.length > 0" class="mt-4">
        <p class="mb-2">
          Si oui, lesquels ?
        </p>
        <div class="flex flex-wrap items-center gap-y-4 gap-x-2">
          <ConsumerProductChip
            v-for="(product, index) in products"
            :key="product.id" class="cursor-pointer" :index="index"
            :product="product"
            :class="{
              'bg-primary text-white': isProductSelected(product)
            }"
            :icon-color="isProductSelected(product) ? 'white' : undefined"
            @click="selectProduct(product)"
          />
        </div>
      </div>
      <div v-if="isYes" class="flex justify-end mt-2">
        <BaseButton primary @click="submit">
          Valider
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
